import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'helpers/api';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import Background from 'components/common/Background';
import SubtleBadge from 'components/common/SubtleBadge';
import isHiddenFacility from 'helpers/isHiddenFacility';
import paths from 'routes/paths';
import corner from 'assets/img/illustrations/corner-4.png';

import AuditSteps from './AuditSteps';
import CsvDropzone from './CsvDropzone';
import ColumnMapping from './ColumnMapping';
import AuditSummary from './AuditSummary';
import AuditResultsTable from './AuditResultsTable';
import { BRAND } from './auditTones';
import { displayTimestamp } from './auditFormat';
import { buildComparisonCsv, buildFindingsCsv, downloadCsv, auditFileName } from './downloadAuditCsv';

/**
 * Medication Auditing.
 *
 * Reconciles a facility's medication export against the active prescriptions we
 * hold for it, and shows where the two disagree. Pick a facility, upload the
 * CSV, confirm how its columns were read, run.
 *
 * READ ONLY. The audit reports differences; it never writes a correction back.
 * Whatever it finds is fixed through the normal prescription screens.
 *
 * The file is held in browser memory rather than uploaded once and cached
 * server-side, because changing the column mapping or toggling strict mode has
 * to re-run the comparison - strict changes which rows PAIR, not just how they
 * are coloured, so it cannot be filtered in the browser.
 *
 * Facility list comes from the `facilitiesData` cache written at login, the
 * same source the other facility reports use (see FacilityReportIndex), minus
 * the hidden AtHome / Day Program facilities.
 *
 * Icons throughout are the FontAwesome SVG component against the library in
 * helpers/initFA.js. This app never loads FontAwesome's stylesheet, so a
 * `fas fa-*` class renders as nothing.
 */

const MAX_ROLE = 4;   // Group Admin and above; the server enforces it too

// A template of the shape the audit expects, served statically from
// public/samples (same pattern as the day-program patient import).
const SAMPLE_CSV_URL = `${process.env.PUBLIC_URL || ''}/samples/medication_audit_sample.csv`;

const StepBadge = ({ children }) => (
  <div
    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-2 fw-bold"
    style={{
      width: 26,
      height: 26,
      backgroundColor: BRAND.soft,
      color: BRAND.base,
      fontSize: '0.78rem'
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ step, title, subtitle, children }) => (
  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
    <div className="d-flex align-items-center">
      <StepBadge>{step}</StepBadge>
      <div>
        <div className="fw-semibold lh-1">{title}</div>
        {subtitle && <div className="fs-11 text-600 mt-1">{subtitle}</div>}
      </div>
    </div>
    {children}
  </div>
);

const MedicationAudit = () => {
  const currentRoleId = Number(localStorage.getItem('roleId'));

  const [fields, setFields] = useState([]);
  const [facility, setFacility] = useState(null);
  const [facilityError, setFacilityError] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [strict, setStrict] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const facilityOptions = useMemo(() => {
    const all = JSON.parse(localStorage.getItem('facilitiesData') || '[]');
    return (Array.isArray(all) ? all : []).filter(f => !isHiddenFacility(f));
  }, []);

  // The field catalogue drives the mapping dropdowns. Served rather than
  // hardcoded here so the UI and the engine can never disagree about what is
  // comparable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/medication-audit/fields');
        if (!cancelled && data && data.status) setFields(data.data.fields || []);
      } catch (e) {
        if (!cancelled) toast.error('Could not load the audit field list.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resetFromFile = () => {
    setPreview(null);
    setOverrides({});
    setResult(null);
    setError('');
  };

  /** Read the file's headers and show how they resolved. No patient data yet. */
  const runPreview = useCallback(async (theFile, mapping) => {
    setPreviewing(true);
    setError('');
    try {
      const form = new FormData();
      form.append('csv_file', theFile);
      if (mapping && Object.keys(mapping).length) form.append('mapping', JSON.stringify(mapping));

      const { data } = await axios.post('/medication-audit/preview', form);
      if (!data || !data.status) throw new Error((data && data.message) || 'Could not read that file');
      setPreview(data.data);
    } catch (e) {
      const message = e?.response?.data?.message || e.message || 'Could not read that file';
      setPreview(null);
      setError(message);
      toast.error(message);
    } finally {
      setPreviewing(false);
    }
  }, []);

  const handleFile = useCallback(
    theFile => {
      resetFromFile();
      setFile(theFile);
      runPreview(theFile, null);
    },
    [runPreview]
  );

  const handleClearFile = () => {
    setFile(null);
    resetFromFile();
  };

  // A correction re-runs the preview so the table shows the mapping that will
  // actually be used, rather than the reviewer's edit sitting unapplied.
  const handleMappingChange = (index, fieldId) => {
    const next = { ...overrides, [index]: fieldId };
    setOverrides(next);
    setResult(null);
    if (file) runPreview(file, next);
  };

  const handleMappingReset = () => {
    setOverrides({});
    setResult(null);
    if (file) runPreview(file, {});
  };

  const runAudit = useCallback(
    async nextStrict => {
      if (!facility) {
        setFacilityError(true);
        toast.error('Choose a facility first.');
        return;
      }
      if (!file) {
        toast.error('Upload a medication export first.');
        return;
      }

      setRunning(true);
      setError('');
      try {
        const form = new FormData();
        form.append('csv_file', file);
        form.append('facility_id', facility.value);
        form.append('strict', nextStrict ? 'true' : 'false');
        if (Object.keys(overrides).length) form.append('mapping', JSON.stringify(overrides));

        const { data } = await axios.post('/medication-audit/run', form);
        if (!data || !data.status) throw new Error((data && data.message) || 'The audit could not be completed');

        setResult(data.data);
        setStrict(!!data.data.strict);
        toast.success('Audit complete');
      } catch (e) {
        const message = e?.response?.data?.message || e.message || 'The audit could not be completed';
        setError(message);
        toast.error(message);
      } finally {
        setRunning(false);
      }
    },
    [facility, file, overrides]
  );

  // Strict changes which rows pair, so it is a re-run, not a display filter.
  const handleStrictChange = value => {
    setStrict(value);
    runAudit(value);
  };

  const busy = previewing || running;
  const canRun = !!facility && !!preview && preview.canRun && !busy;
  const step = result ? 'results' : preview ? 'mapping' : 'source';

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Medication Auditing', href: '', isActive: true }
  ];

  if (currentRoleId > MAX_ROLE) {
    return <Navigate to={paths.accessDenied} replace />;
  }

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />

      {/* Hero + progress in one card: three stacked cards pushed the actual form
          below the fold for no gain. */}
      <Card className="mb-3 overflow-hidden">
        <Background image={corner} className="d-none d-md-block bg-card" />
        <Card.Body className="position-relative pb-0">
          <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
            <h4 className="mb-0">Medication Auditing</h4>
            <SubtleBadge bg="success" pill>
              <FontAwesomeIcon icon="lock" className="me-1" />
              Read only
            </SubtleBadge>
          </div>
          <p className="mb-0 text-700 fs-10" style={{ maxWidth: 620 }}>
            Compare a facility&apos;s medication export against the prescriptions MapYourMeds holds
            for it, field by field. Nothing is written back — the audit only reports what differs,
            so it is safe to run against live data.
          </p>
        </Card.Body>
        <Card.Body className="position-relative pt-3">
          <div className="pt-3 border-top">
            <AuditSteps current={step} />
          </div>
        </Card.Body>
      </Card>

      {/* Step 1 - what to audit. */}
      <Card className="mb-3">
        <Card.Body>
          <SectionTitle
            step="1"
            title="Choose what to audit"
            subtitle="The facility these medications belong to, and the export to check it against"
          />
          <Row className="g-3 align-items-stretch">
            <Col xs={12} lg={5}>
              <Form.Group className="h-100 d-flex flex-column">
                <Form.Label className="fw-medium">
                  Facility <span className="text-danger">*</span>
                </Form.Label>
                <ThemedSelect
                  name="facility"
                  placeholder="Select facility"
                  closeMenuOnSelect
                  classNamePrefix="react-select"
                  options={facilityOptions}
                  value={facility}
                  isDisabled={busy}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => option.value}
                  onChange={option => {
                    setFacility(option);
                    setFacilityError(false);
                    setResult(null);
                  }}
                />
                {facilityError && (
                  <div className="text-danger fs-10 mt-1">
                    <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
                    Choose the facility this export belongs to.
                  </div>
                )}
                {facilityOptions.length === 0 && (
                  <div className="text-warning fs-10 mt-1">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                    No facilities are available on this account.
                  </div>
                )}
                {/* Fills the column to the dropzone's height. Without something
                    here the left side ends at the select and leaves a void
                    beside a much taller box. */}
                {facility ? (
                  <div
                    className="mt-3 p-3 rounded-3 flex-grow-1 d-flex flex-column justify-content-center"
                    style={{ backgroundColor: BRAND.softer, borderLeft: `3px solid ${BRAND.base}` }}
                  >
                    <div className="fs-11 text-600 text-uppercase fw-medium mb-1">Auditing against</div>
                    <div className="fw-semibold" style={{ color: BRAND.base }}>
                      {facility.key}
                    </div>
                    <div className="fs-11 text-600 mt-1">
                      Its active prescriptions are what the export is checked against
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 p-3 rounded-3 border flex-grow-1 d-flex flex-column justify-content-center">
                    <div className="fs-11 text-600 text-uppercase fw-medium mb-2">What happens next</div>
                    {[
                      { icon: 'prescription', text: 'The facility’s active prescriptions are loaded' },
                      { icon: 'link', text: 'Each export row is matched to one of them' },
                      { icon: 'table', text: 'Every field is compared and differences highlighted' }
                    ].map(item => (
                      <div key={item.icon} className="d-flex align-items-start mb-2">
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="me-2 mt-1 flex-shrink-0"
                          style={{ fontSize: '0.72rem', color: '#9da9bb' }}
                        />
                        <span className="fs-10 text-700">{item.text}</span>
                      </div>
                    ))}
                    <div className="fs-11 text-500 mt-1">Nothing is changed in MapYourMeds.</div>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} lg={7} className="d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <Form.Label className="fw-medium mb-0">
                  Medication export <span className="text-500 fw-normal">(.csv)</span>
                </Form.Label>
                <a
                  className="fs-11 text-decoration-none"
                  style={{ color: BRAND.base }}
                  href={SAMPLE_CSV_URL}
                  download="medication_audit_sample.csv"
                >
                  <FontAwesomeIcon icon="download" className="me-1" />
                  Download a sample CSV
                </a>
              </div>
              <CsvDropzone
                file={file}
                onFile={handleFile}
                onClear={handleClearFile}
                disabled={busy}
                rowCount={preview ? preview.rowCount : null}
              />

              {/* What the file turned out to contain. Sits in what would
                  otherwise be dead space beside the facility panel. */}
              {preview && (
                <Row className="g-2 mt-1">
                  {[
                    { label: 'Columns found', value: preview.headers.length, icon: 'table' },
                    {
                      label: 'Will be compared',
                      value: Object.keys(preview.mapping || {}).length,
                      icon: 'check-circle',
                      good: true
                    },
                    {
                      label: 'No field with us',
                      value: (preview.unsupported || []).length,
                      icon: 'minus'
                    },
                    {
                      label: 'Not recognised',
                      value: (preview.unmapped || []).length,
                      icon: 'exclamation-triangle',
                      warn: (preview.unmapped || []).length > 0
                    }
                  ].map(item => (
                    <Col xs={6} sm={3} key={item.label}>
                      <div className="border rounded-3 px-2 py-2 h-100 text-center">
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="mb-1"
                          style={{
                            fontSize: '0.8rem',
                            color: item.warn ? '#b3671a' : item.good ? '#00864e' : '#9da9bb'
                          }}
                        />
                        <div
                          className="fw-bold lh-1"
                          style={{ color: item.warn ? '#b3671a' : undefined }}
                        >
                          {item.value}
                        </div>
                        <div className="fs-11 text-600 mt-1">{item.label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0 py-2 d-flex align-items-start">
              <FontAwesomeIcon icon="exclamation-circle" className="me-2 mt-1 flex-shrink-0" />
              <div className="fs-10">{error}</div>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {previewing && !preview && (
        <Card className="mb-3">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-2" />
            <div className="text-600">Reading the file…</div>
          </Card.Body>
        </Card>
      )}

      {/* Step 2 - confirm the columns. */}
      {preview && (
        <Card className="mb-3">
          <Card.Body>
            <SectionTitle
              step="2"
              title="Confirm the column mapping"
              subtitle="Detected from the headers — correct anything read wrongly before running"
            />
            <ColumnMapping
              preview={preview}
              fields={fields}
              overrides={overrides}
              onChange={handleMappingChange}
              onReset={handleMappingReset}
              disabled={busy}
            />
            <div className="d-flex justify-content-end align-items-center gap-3 mt-3 pt-3 border-top">
              {!facility && (
                <span className="text-600 fs-10">
                  <FontAwesomeIcon icon="arrow-up" className="me-1" />
                  Choose a facility above to run the audit
                </span>
              )}
              <Button variant="primary" size="lg" onClick={() => runAudit(strict)} disabled={!canRun}>
                {running ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Auditing…
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="balance-scale-left" className="me-2" />
                    Run audit
                  </>
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Step 3 - what differs. */}
      {result && (
        <Card>
          <Card.Header className="bg-body-tertiary border-bottom">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <StepBadge>3</StepBadge>
                <div>
                  <div className="fw-semibold lh-1">{result.facility.name}</div>
                  <div className="fs-11 text-600 mt-1">
                    {result.fileName} · {displayTimestamp(result.generatedAt)}
                  </div>
                </div>
              </div>
              <SubtleBadge bg={strict ? 'warning' : 'info'} pill>
                {strict ? 'Strict comparison' : 'Clinical comparison'}
              </SubtleBadge>
            </div>
          </Card.Header>
          <Card.Body>
            <AuditSummary
              result={result}
              strict={strict}
              busy={busy}
              onStrictChange={handleStrictChange}
              onDownloadFindings={() =>
                downloadCsv(buildFindingsCsv(result), auditFileName(result, 'findings'))
              }
              onDownloadComparison={() =>
                downloadCsv(buildComparisonCsv(result), auditFileName(result, 'comparison'))
              }
            />
            <AuditResultsTable result={result} />
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default MedicationAudit;
